import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  deleteModelBibleEntry,
  getApiErrorMessage,
  getModelBibleEntries,
  importModelBibleEntries,
  previewModelBibleFacts,
  updateModelBibleEntry,
} from '../api'
import '../styles/components/model-bible-panel.css'

const CATEGORY_OPTIONS = [
  ['general', 'General'],
  ['bio', 'Bio'],
  ['likes', 'Likes'],
  ['dislikes', 'Dislikes'],
  ['location', 'Location'],
  ['movies', 'Movies'],
  ['music', 'Music'],
  ['hobbies', 'Hobbies'],
  ['food', 'Food'],
  ['style', 'Style'],
  ['boundaries', 'Boundaries'],
  ['sales', 'Sales'],
  ['content', 'Content'],
]

function categoryLabel(value) {
  return CATEGORY_OPTIONS.find(([key]) => key === value)?.[1] || value || 'General'
}

export default function ModelBiblePanel({ modelId, variant = 'classic' }) {
  const isV2 = variant === 'v2'
  const [entries, setEntries] = useState([])
  const [drafts, setDrafts] = useState({})
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [status, setStatus] = useState('')
  const [importText, setImportText] = useState('')
  const [importCategory, setImportCategory] = useState('general')
  const [replaceExisting, setReplaceExisting] = useState(false)
  const [previewQuery, setPreviewQuery] = useState('')
  const [previewEntries, setPreviewEntries] = useState([])

  const buttonBase = isV2 ? 'v2-btn' : 'btn btn-sm'
  const primaryButton = isV2 ? 'v2-btn v2-btn-primary' : 'btn btn-primary btn-sm'
  const secondaryButton = isV2 ? 'v2-btn v2-btn-secondary' : 'btn btn-secondary btn-sm'
  const ghostButton = isV2 ? 'v2-btn v2-btn-ghost' : 'btn btn-ghost btn-sm'

  const loadEntries = useCallback(async () => {
    if (!modelId) return
    setLoading(true)
    setError('')
    try {
      const result = await getModelBibleEntries(modelId)
      const rows = Array.isArray(result) ? result : []
      setEntries(rows)
      setDrafts(Object.fromEntries(rows.map((entry) => [entry.id, {
        title: entry.title || '',
        category: entry.category || 'general',
        content: entry.content || '',
        priority: entry.priority || 0,
        is_active: entry.is_active !== false,
      }])))
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to load model bible'))
    } finally {
      setLoading(false)
    }
  }, [modelId])

  useEffect(() => {
    loadEntries()
  }, [loadEntries])

  const activeCount = useMemo(
    () => entries.filter((entry) => entry.is_active !== false).length,
    [entries],
  )

  function updateDraft(entryId, key, value) {
    setDrafts((current) => ({
      ...current,
      [entryId]: {
        ...(current[entryId] || {}),
        [key]: value,
      },
    }))
  }

  async function handleImport(event) {
    event.preventDefault()
    if (!modelId || !importText.trim()) return
    setBusy(true)
    setError('')
    setStatus('')
    try {
      const result = await importModelBibleEntries(modelId, {
        text: importText,
        category: importCategory,
        replace: replaceExisting,
      })
      setImportText('')
      setReplaceExisting(false)
      setStatus(`${result.created || 0} facts imported`)
      await loadEntries()
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to import model bible facts'))
    } finally {
      setBusy(false)
    }
  }

  async function handleSave(entryId) {
    const draft = drafts[entryId]
    if (!modelId || !draft || !String(draft.content || '').trim()) return
    setBusy(true)
    setError('')
    setStatus('')
    try {
      await updateModelBibleEntry(modelId, entryId, draft)
      setStatus('Fact saved')
      await loadEntries()
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to save model bible fact'))
    } finally {
      setBusy(false)
    }
  }

  async function handleToggle(entry) {
    const current = drafts[entry.id] || entry
    updateDraft(entry.id, 'is_active', !current.is_active)
    setBusy(true)
    setError('')
    setStatus('')
    try {
      await updateModelBibleEntry(modelId, entry.id, { is_active: !current.is_active })
      await loadEntries()
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to update model bible fact'))
    } finally {
      setBusy(false)
    }
  }

  async function handleDelete(entryId) {
    if (!modelId) return
    setBusy(true)
    setError('')
    setStatus('')
    try {
      await deleteModelBibleEntry(modelId, entryId)
      setStatus('Fact deleted')
      await loadEntries()
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to delete model bible fact'))
    } finally {
      setBusy(false)
    }
  }

  async function handlePreview(event) {
    event.preventDefault()
    if (!modelId || !previewQuery.trim()) return
    setBusy(true)
    setError('')
    setPreviewEntries([])
    try {
      const result = await previewModelBibleFacts(modelId, { query: previewQuery, limit: 5 })
      setPreviewEntries(Array.isArray(result?.entries) ? result.entries : [])
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to preview model bible facts'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={`model-bible-panel ${isV2 ? 'model-bible-panel--v2' : ''}`}>
      <div className="model-bible-panel__summary">
        <div>
          <strong>Optional model bible</strong>
          <p>Store facts like favorite movies, places, hobbies and boundaries. Facts are retrieved only when they match the current fan message.</p>
        </div>
        <span>{activeCount} active / {entries.length} total</span>
      </div>

      {error ? <div className={isV2 ? 'v2-error model-bible-panel__notice' : 'model-bible-panel__error'}>{error}</div> : null}
      {status ? <div className={isV2 ? 'v2-success model-bible-panel__notice' : 'model-bible-panel__status'}>{status}</div> : null}

      <form className="model-bible-panel__import" onSubmit={handleImport}>
        <label>
          <span>Import facts</span>
          <textarea
            value={importText}
            onChange={(event) => setImportText(event.target.value)}
            rows={5}
            placeholder="One fact per line. Example: Movies: Vesna likes slow European cinema and romantic comedies."
          />
        </label>
        <div className="model-bible-panel__controls">
          <label>
            <span>Default category</span>
            <select value={importCategory} onChange={(event) => setImportCategory(event.target.value)}>
              {CATEGORY_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </label>
          <label className="model-bible-panel__check">
            <input type="checkbox" checked={replaceExisting} onChange={(event) => setReplaceExisting(event.target.checked)} />
            <span>Replace existing facts</span>
          </label>
          <button type="submit" className={primaryButton} disabled={busy || !importText.trim()}>
            {busy ? 'Saving...' : 'Import facts'}
          </button>
        </div>
      </form>

      <form className="model-bible-panel__preview" onSubmit={handlePreview}>
        <label>
          <span>Preview retrieval</span>
          <input
            value={previewQuery}
            onChange={(event) => setPreviewQuery(event.target.value)}
            placeholder="Example: what movies do you like?"
          />
        </label>
        <button type="submit" className={secondaryButton} disabled={busy || !previewQuery.trim()}>
          Preview
        </button>
      </form>
      {previewEntries.length ? (
        <div className="model-bible-panel__preview-results">
          {previewEntries.map((entry) => (
            <span key={entry.id}>{categoryLabel(entry.category)}: {entry.content}</span>
          ))}
        </div>
      ) : null}

      <div className="model-bible-panel__entries">
        {loading ? <p>Loading facts...</p> : null}
        {!loading && !entries.length ? (
          <p className="model-bible-panel__empty">No bible facts yet. This is optional; the bot will keep using the current model profile.</p>
        ) : null}
        {entries.map((entry) => {
          const draft = drafts[entry.id] || entry
          return (
            <article key={entry.id} className={`model-bible-entry ${draft.is_active === false ? 'is-inactive' : ''}`}>
              <div className="model-bible-entry__meta">
                <select value={draft.category || 'general'} onChange={(event) => updateDraft(entry.id, 'category', event.target.value)}>
                  {CATEGORY_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
                <input
                  value={draft.title || ''}
                  onChange={(event) => updateDraft(entry.id, 'title', event.target.value)}
                  placeholder="Optional title"
                />
                <input
                  type="number"
                  value={draft.priority || 0}
                  onChange={(event) => updateDraft(entry.id, 'priority', event.target.value)}
                  title="Priority"
                />
              </div>
              <textarea
                value={draft.content || ''}
                onChange={(event) => updateDraft(entry.id, 'content', event.target.value)}
                rows={3}
              />
              <div className="model-bible-entry__actions">
                <button type="button" className={buttonBase} onClick={() => handleSave(entry.id)} disabled={busy}>
                  Save
                </button>
                <button type="button" className={ghostButton} onClick={() => handleToggle(entry)} disabled={busy}>
                  {draft.is_active === false ? 'Enable' : 'Disable'}
                </button>
                <button type="button" className={ghostButton} onClick={() => handleDelete(entry.id)} disabled={busy}>
                  Delete
                </button>
              </div>
            </article>
          )
        })}
      </div>
    </div>
  )
}
